import humanizeDuration from "humanize-duration";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock3,
  PlugZap,
  Server,
  UserPlus,
} from "lucide-react";
import type React from "react";

import {
  type ConnectorStatus,
  ServiceStatus,
  type StatusSignal,
  type StatusSnapshot,
  type StatusSummary,
} from "@elation/api-client/src/stitch-web/models";

import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { Badge } from "@src/shared/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@src/shared/components/ui/card";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { cn } from "@src/shared/lib/utils";

export interface StatusPageProps extends StatusSnapshot {
  isLoading?: boolean;
  error?: string | null;
}

const overallStatusPriority = [
  ServiceStatus.outage,
  ServiceStatus.degraded,
  ServiceStatus.maintenance,
  ServiceStatus.unknown,
  ServiceStatus.operational,
];

const statusDisplay: Record<
  ServiceStatus,
  {
    label: string;
    tone: string;
    badgeVariant: React.ComponentProps<typeof Badge>["variant"];
    icon: typeof CheckCircle2;
  }
> = {
  operational: {
    label: "Operational",
    tone: "border-success/40 bg-success/10 text-success",
    badgeVariant: "success",
    icon: CheckCircle2,
  },
  degraded: {
    label: "Degraded",
    tone: "border-warning/40 bg-warning/10 text-warning",
    badgeVariant: "warning",
    icon: AlertCircle,
  },
  outage: {
    label: "Outage",
    tone: "border-destructive/40 bg-destructive/10 text-destructive",
    badgeVariant: "destructive",
    icon: AlertCircle,
  },
  maintenance: {
    label: "Maintenance",
    tone: "border-accent/40 bg-accent/10 text-accent",
    badgeVariant: "accent",
    icon: Clock3,
  },
  unknown: {
    label: "Unknown",
    tone: "border-muted bg-muted/60 text-muted-foreground",
    badgeVariant: "subtle",
    icon: AlertCircle,
  },
};

const getOverallStatus = (
  stitchWeb: StatusSummary,
  connectors: ConnectorStatus[],
  accountProvisioning?: StatusSummary,
): ServiceStatus => {
  const statuses = [
    stitchWeb.status,
    ...connectors.map(({ status }) => status),
    ...(accountProvisioning ? [accountProvisioning.status] : []),
  ];
  return (
    overallStatusPriority.find((status) => statuses.includes(status)) ??
    "unknown"
  );
};

const formatLatency = (latencyMs?: number) => {
  if (latencyMs === undefined) return "Not checked";
  return `${latencyMs} ms`;
};

const formatLastChecked = (isoTimestamp?: string, fallback = "Pending") => {
  if (!isoTimestamp) return fallback;

  const checkedAt = new Date(isoTimestamp);
  if (Number.isNaN(checkedAt.getTime())) return isoTimestamp;

  const elapsedMs = Date.now() - checkedAt.getTime();
  if (elapsedMs < 1000) return "Just now";

  const humanized = humanizeDuration(elapsedMs, {
    largest: 2,
    round: true,
    units: ["d", "h", "m", "s"],
  });
  return `${humanized} ago`;
};

const StatusBadge = ({ status }: { status: ServiceStatus }) => {
  const display = statusDisplay[status];
  const Icon = display.icon;

  return (
    <Badge variant={display.badgeVariant} className="gap-1.5">
      <Icon className="size-3.5" />
      {display.label}
    </Badge>
  );
};

const StatusIcon = ({ status }: { status: ServiceStatus }) => {
  const display = statusDisplay[status];
  const Icon = display.icon;

  return (
    <div
      className={cn(
        "flex size-11 items-center justify-center rounded-full border",
        display.tone,
      )}
    >
      <Icon className="size-5" />
    </div>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border border-border bg-background/80 p-3">
    <Text
      as="div"
      sizing="xs"
      appearance="subdued"
      className="uppercase tracking-wide"
    >
      {label}
    </Text>
    <Text as="div" sizing="sm" fontWeight={600} className="mt-1">
      {value}
    </Text>
  </div>
);

const SignalList = ({ signals }: { signals?: StatusSignal[] }) => {
  if (!signals?.length) return null;

  return (
    <div className="grid gap-2">
      {signals.map((signal) => (
        <div
          key={signal.label}
          className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 px-3 py-2"
        >
          <Text sizing="sm" appearance="subdued">
            {signal.label}
          </Text>
          <div className="flex items-center gap-2 text-right">
            <Text sizing="sm" fontWeight={600}>
              {signal.value}
            </Text>
            {signal.status ? <StatusBadge status={signal.status} /> : null}
          </div>
        </div>
      ))}
    </div>
  );
};

const ServiceCard = ({
  service,
  icon: Icon,
  children,
}: {
  service: StatusSummary;
  icon: typeof Server;
  children?: React.ReactNode;
}) => (
  <Card className="overflow-hidden border-border shadow-soft">
    <CardHeader className="border-b border-border bg-card/80">
      <div className="flex flex-col gap-4 sm-stitch:flex-row sm-stitch:items-start sm-stitch:justify-between">
        <div className="flex gap-3">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="size-5" />
          </div>
          <div>
            <CardTitle className="text-xl">{service.name}</CardTitle>
            <CardDescription className="mt-1 max-w-2xl">
              {service.description}
            </CardDescription>
          </div>
        </div>
        <StatusBadge status={service.status} />
      </div>
    </CardHeader>
    <CardContent className="grid gap-5 p-5">
      {service.details ? (
        <Text sizing="sm" appearance="subdued">
          {service.details}
        </Text>
      ) : null}
      <div className="grid gap-3 sm-stitch:grid-cols-2">
        <Metric label="Latency" value={formatLatency(service.latencyMs)} />
        <Metric
          label="Last check"
          value={formatLastChecked(service.lastCheckedAt)}
        />
      </div>
      <SignalList signals={service.signals} />
      {children}
    </CardContent>
  </Card>
);

const ConnectorCard = ({ connector }: { connector: ConnectorStatus }) => (
  <Card className="h-full border-border shadow-soft transition-shadow hover:shadow-card">
    <CardHeader>
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <StatusIcon status={connector.status} />
          <div>
            <CardTitle>{connector.vendor}</CardTitle>
            <CardDescription className="mt-1">
              {connector.description}
            </CardDescription>
          </div>
        </div>
        <StatusBadge status={connector.status} />
      </div>
    </CardHeader>
    <CardContent className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">Connector {connector.connectorId}</Badge>
        {connector.environment ? (
          <Badge variant="subtle">{connector.environment}</Badge>
        ) : null}
      </div>
      {connector.details ? (
        <Text sizing="sm" appearance="subdued">
          {connector.details}
        </Text>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <Metric label="Latency" value={formatLatency(connector.latencyMs)} />
        <Metric
          label="Last check"
          value={formatLastChecked(connector.lastCheckedAt)}
        />
      </div>
      <SignalList signals={connector.signals} />
    </CardContent>
  </Card>
);

const LoadingState = () => (
  <div className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-8 md-stitch:px-8">
    <Skeleton className="h-36 w-full rounded-2xl" />
    <Skeleton className="h-64 w-full rounded-xl" />
    <div className="grid gap-4 lg-stitch:grid-cols-2">
      <Skeleton className="h-72 w-full rounded-xl" />
      <Skeleton className="h-72 w-full rounded-xl" />
    </div>
  </div>
);

export const StatusPage = ({
  stitchWeb,
  connectors,
  checkedAt,
  accountProvisioning,
  isLoading = false,
  error = null,
}: StatusPageProps) => {
  if (isLoading) {
    return <LoadingState />;
  }

  const overallStatus = getOverallStatus(
    stitchWeb,
    connectors,
    accountProvisioning,
  );
  const overallDisplay = statusDisplay[overallStatus];

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-8 md-stitch:px-8 md-stitch:py-10">
        <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
          <div className="relative p-6 md-stitch:p-8">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-accent via-success to-warning" />
            <div className="flex flex-col gap-6 lg-stitch:flex-row lg-stitch:items-center lg-stitch:justify-between">
              <div className="max-w-3xl">
                <Text
                  sizing="sm"
                  fontWeight={600}
                  appearance="subdued"
                  className="uppercase tracking-widest"
                >
                  Status
                </Text>
                <HeaderText tag="h1" className="mt-2 text-4xl">
                  Stitch system status
                </HeaderText>
                <Text appearance="subdued" className="mt-3 max-w-2xl">
                  Current availability for Stitch and each configured EHR
                  connector.
                </Text>
              </div>
              <div
                className={cn(
                  "rounded-2xl border px-5 py-4 text-right",
                  overallDisplay.tone,
                )}
              >
                <Text
                  as="div"
                  sizing="xs"
                  fontWeight={600}
                  className="uppercase"
                >
                  Overall
                </Text>
                <div className="mt-2 flex items-center justify-end gap-2">
                  <StatusIcon status={overallStatus} />
                  <div>
                    <Text as="div" fontWeight={700} className="text-lg">
                      {overallDisplay.label}
                    </Text>
                    <Text as="div" sizing="xs">
                      Updated{" "}
                      {formatLastChecked(
                        checkedAt ?? stitchWeb.lastCheckedAt,
                        "pending",
                      )}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
            {error ? (
              <div className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-destructive">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 size-5" />
                  <div>
                    <Text fontWeight={700}>Unable to refresh live status</Text>
                    <Text sizing="sm" className="mt-1">
                      {error}
                    </Text>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="grid gap-3" aria-labelledby="stitch-web-status">
          <div>
            <Text
              sizing="sm"
              fontWeight={600}
              appearance="subdued"
              className="uppercase tracking-widest"
            >
              Stitch
            </Text>
            <HeaderText tag="h2" id="stitch-web-status" className="mt-1">
              Application health
            </HeaderText>
          </div>
          <ServiceCard service={stitchWeb} icon={Server}>
            <div className="rounded-xl bg-muted/50 p-4">
              <div className="flex items-center gap-2">
                <Activity className="size-4 text-accent" />
                <Text sizing="sm" fontWeight={600}>
                  Monitoring app availability, API responsiveness, and core
                  background jobs.
                </Text>
              </div>
            </div>
          </ServiceCard>
        </section>

        {accountProvisioning ? (
          <section
            className="grid gap-3"
            aria-labelledby="account-provisioning-status"
          >
            <div>
              <Text
                sizing="sm"
                fontWeight={600}
                appearance="subdued"
                className="uppercase tracking-widest"
              >
                Account provisioning
              </Text>
              <HeaderText
                tag="h2"
                id="account-provisioning-status"
                className="mt-1"
              >
                Provisioning health
              </HeaderText>
            </div>
            <ServiceCard service={accountProvisioning} icon={UserPlus} />
          </section>
        ) : null}

        <section className="grid gap-3" aria-labelledby="connector-status">
          <div className="flex flex-col gap-2 sm-stitch:flex-row sm-stitch:items-end sm-stitch:justify-between">
            <div>
              <Text
                sizing="sm"
                fontWeight={600}
                appearance="subdued"
                className="uppercase tracking-widest"
              >
                EHR Connections
              </Text>
              <HeaderText tag="h2" id="connector-status" className="mt-1">
                Health
              </HeaderText>
            </div>
            <Text sizing="sm" appearance="subdued">
              {connectors.length} connector
              {connectors.length === 1 ? "" : "s"} monitored
            </Text>
          </div>
          {connectors.length > 0 ? (
            <div className="grid gap-4 lg-stitch:grid-cols-2">
              {connectors.map((connector) => (
                <ConnectorCard
                  key={connector.connectorId}
                  connector={connector}
                />
              ))}
            </div>
          ) : (
            <Card className="border-dashed border-border bg-card/70">
              <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                <PlugZap className="size-10 text-muted-foreground" />
                <div>
                  <CardTitle>No connectors configured</CardTitle>
                  <CardDescription className="mt-2">
                    Connector status cards will appear here once an EHR
                    connector is available to monitor.
                  </CardDescription>
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
};
